/* ==========================================================================
   Warming the icon cache while the machine boots.

   Every icon on this desktop is its own little file — `Icon` resolves a name
   and a size to one of the extracted XP bitmaps, which is the whole reason the
   16px Recycle Bin looks like the 16px Recycle Bin. The cost of that is a
   request per icon per size, paid the first time each one is shown: open a
   folder you have not opened before and its toolbar, its task pane and its
   files all reach for files nobody has asked for yet. Over a real connection
   that is the half-second of blank squares.

   The fix is not to bundle them — the point of `/assets` is that it is served
   verbatim and cached for a month — but to ask for them *earlier*. Boot and
   the Welcome screen are several seconds of deliberate idling with nothing on
   the wire, which is exactly the budget this needs: the whole set is around
   230 KB, and by the time the desktop paints it is already in cache.
   ========================================================================== */

import { ICON_ASSETS, ICON_PNG_DIR } from '@/config/iconAssets';

/**
 * Icons drawn as SVG because no XP bitmap was extracted for them — see
 * `scripts/xp-icons.json`. They are listed by hand because there is nothing to
 * derive them from: `ICON_ASSETS` is a record of what *was* extracted, and the
 * rest of the folder is only ever named from inside a component.
 *
 * `precache.test.ts` fails if any of these gains a bitmap, which is the moment
 * this list would start fetching a file nothing loads any more.
 */
export const SVG_ONLY_ICONS: readonly string[] = [
  'folders',
  'hourglass',
  'page',
  'playlist',
  'run',
  'views',
  'visualizer',
  'winamp',
];

/** Shell furniture outside `/assets/icons` that is on screen from the first frame. */
const SHELL_ASSETS: readonly string[] = [
  '/assets/ui/windows-flag.svg',
  '/assets/ui/user-tile.svg',
];

/**
 * How many requests to keep in the air.
 *
 * Not unbounded: this runs *while* the boot screen is animating and the app's
 * own chunks are still arriving, and 140 simultaneous requests would push both
 * down the queue on exactly the slow connection this is meant to help. Six is
 * about what a browser would give a single origin over HTTP/1.1 anyway.
 */
const CONCURRENCY = 6;

/** Every file worth having in cache before the desktop appears. */
export function precacheUrls(): string[] {
  const urls: string[] = [];

  for (const [name, sizes] of Object.entries(ICON_ASSETS)) {
    for (const size of sizes) urls.push(`${ICON_PNG_DIR}/${name}-${size}.png`);
  }
  for (const name of SVG_ONLY_ICONS) urls.push(`/assets/icons/${name}.svg`);
  urls.push(...SHELL_ASSETS);

  return urls;
}

/**
 * True when the visitor has told us not to spend their data — Data Saver, or a
 * connection the browser rates 2G or worse. Warming a cache is a courtesy; it
 * is not worth overriding somebody's explicit "don't".
 *
 * `connection` is Chromium-only and every field of it is optional, hence the
 * shape rather than a cast to something that claims more than it knows.
 */
function shouldSkip(): boolean {
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  };
  const c = nav.connection;
  if (!c) return false;
  if (c.saveData) return true;
  return c.effectiveType === 'slow-2g' || c.effectiveType === '2g';
}

/** Resolves when the image has loaded, or failed — a miss must not stall the queue. */
function warm(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    // Behind the boot screen, the app's own chunks and the wallpaper prefetch.
    img.fetchPriority = 'low';
    img.decoding = 'async';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

/** Per document, not per mount: `BootController` remounts on a reboot. */
let started = false;

/**
 * Pull the icon set into cache, `CONCURRENCY` files at a time.
 *
 * Fire-and-forget by design: nothing waits on it, nothing reports it, and if
 * the visitor clicks through the Welcome screen before it finishes the rest
 * simply keeps arriving behind the desktop. Idempotent, so calling it from a
 * component that may mount twice (StrictMode, a reboot) is safe.
 */
export function precacheShellAssets(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (shouldSkip()) return;

  const queue = precacheUrls();
  let next = 0;

  const pump = (): Promise<void> => {
    const url = queue[next++];
    if (url === undefined) return Promise.resolve();
    return warm(url).then(pump);
  };

  const run = () => {
    for (let lane = 0; lane < CONCURRENCY; lane += 1) void pump();
  };

  // After the first paint of whichever screen is up, so the boot animation is
  // on screen before anything competes with it for the connection. Safari only
  // shipped `requestIdleCallback` in 2022, hence the timeout fallback.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 1000 });
  } else {
    window.setTimeout(run, 200);
  }
}
